/**
 * ReferralPage - Main referral dashboard page.
 * Displays referral code, stats, history, rewards, and how-it-works section.
 */

import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { trackEvent } from "@jumpbean/analytics";
import { usePageTitle } from "../hooks/usePageTitle";
import { useReferrals } from "../hooks/useReferrals";
import { PageLoader } from "../components/Loader";
import { Card } from "../components/Card";
import { Button } from "../components/Button";
import {
  ReferralCodeCard,
  ReferralStats,
  ReferralHistory,
  RewardsList,
  ShareModal,
} from "../components/referrals";

export function ReferralPage() {
  usePageTitle("Refer Friends");

  const {
    code,
    stats,
    history,
    rewards,
    isLoading,
    error,
    isGenerating,
    generateCode,
    refetch,
  } = useReferrals();

  const [showShareModal, setShowShareModal] = useState(false);

  // Track page view when loading completes
  useEffect(() => {
    if (!isLoading) {
      trackEvent("referral_page_viewed", { has_code: !!code });
    }
  }, [isLoading, code]);

  // Loading state
  if (isLoading) {
    return <PageLoader message="Loading your referrals..." />;
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card variant="elevated" className="max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 mx-auto bg-fairy-100 rounded-full flex items-center justify-center mb-4">
            <ExclamationIcon className="w-8 h-8 text-fairy-500" />
          </div>
          <h2 className="font-display font-bold text-display-sm text-neutral-900 mb-2">
            Oops! Something went wrong
          </h2>
          <p className="text-body-md text-neutral-600 mb-6">{error}</p>
          <Button variant="primary" onClick={refetch}>
            Try Again
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen pb-32">
      {/* Background decorations */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-20 -left-20 w-64 h-64 bg-sunshine-200/30 rounded-full blur-3xl" />
        <div className="absolute top-40 -right-32 w-80 h-80 bg-fairy-200/30 rounded-full blur-3xl" />
        <div className="absolute bottom-32 left-1/4 w-72 h-72 bg-magic-200/20 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 px-4 py-8">
        <div className="max-w-2xl mx-auto space-y-8">
          {/* Header */}
          <header className="flex items-center justify-between">
            <Link
              to="/parent"
              className="
                flex items-center gap-2
                px-4 py-2
                font-display font-bold text-body-md text-neutral-600
                rounded-full border-2 border-neutral-200
                bg-cloud
                transition-all duration-200
                hover:border-neutral-300 hover:bg-neutral-50
                active:scale-95
              "
            >
              <ArrowLeftIcon className="w-5 h-5" />
              Back
            </Link>
            <h1 className="font-display font-bold text-display-sm text-neutral-900">
              Refer Friends
            </h1>
            <div className="w-20" /> {/* Spacer for centering */}
          </header>

          {/* Referral Code Card */}
          <section>
            <ReferralCodeCard
              code={code}
              isGenerating={isGenerating}
              onGenerateCode={generateCode}
              onShare={() => setShowShareModal(true)}
            />
          </section>

          {/* Stats Section */}
          {code && (
            <section className="space-y-4">
              <SectionHeader icon={ChartIcon} title="Your Rewards" />
              <ReferralStats stats={stats} />
            </section>
          )}

          {/* Referral History */}
          {code && (
            <section className="space-y-4">
              <SectionHeader icon={UsersIcon} title="Referral History" />
              <ReferralHistory referrals={history} />
            </section>
          )}

          {/* Rewards List - only show if there are rewards */}
          {rewards.length > 0 && (
            <section className="space-y-4">
              <SectionHeader icon={GiftIcon} title="Your Rewards" />
              <RewardsList rewards={rewards} />
            </section>
          )}

          {/* How It Works */}
          <section className="space-y-4">
            <SectionHeader icon={SparkleIcon} title="How It Works" />
            <HowItWorks />
          </section>
        </div>
      </div>

      {/* Share Modal */}
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        code={code}
      />
    </div>
  );
}

// Section header component
function SectionHeader({
  icon: Icon,
  title,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
}) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sunshine-100 to-sunshine-200 flex items-center justify-center">
        <Icon className="w-5 h-5 text-sunshine-600" />
      </div>
      <h2 className="font-display font-bold text-body-xl text-neutral-800">
        {title}
      </h2>
    </div>
  );
}

// How It Works component
function HowItWorks() {
  const steps = [
    {
      number: 1,
      title: "Share Your Code",
      description: "Send your unique referral code to friends and family.",
      icon: ShareIcon,
      color: "from-sunshine-400 to-sunshine-500",
    },
    {
      number: 2,
      title: "They Sign Up",
      description: "Friends create an account and enter your code.",
      icon: UserPlusIcon,
      color: "from-fairy-400 to-fairy-500",
    },
    {
      number: 3,
      title: "Everyone Wins",
      description: "When they subscribe, you both get a free month!",
      icon: HeartIcon,
      color: "from-meadow-400 to-meadow-500",
    },
  ];

  return (
    <Card variant="elevated" className="overflow-hidden">
      <div className="divide-y divide-neutral-100">
        {steps.map((step, index) => (
          <div
            key={step.number}
            className="
              flex items-center gap-5
              p-5
              transition-colors duration-200
              hover:bg-neutral-50
            "
            style={{ animationDelay: `${index * 100}ms` }}
          >
            {/* Number badge */}
            <div
              className={`
                flex-shrink-0
                w-14 h-14 rounded-2xl
                bg-gradient-to-br ${step.color}
                flex items-center justify-center
                shadow-md
              `}
            >
              <step.icon className="w-7 h-7 text-white" />
            </div>

            {/* Content */}
            <div>
              <p className="font-display font-bold text-body-lg text-neutral-800">
                {step.title}
              </p>
              <p className="text-body-md text-neutral-500">{step.description}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Fine print */}
      <div className="px-5 py-4 bg-neutral-50 border-t border-neutral-100">
        <p className="text-body-sm text-neutral-500 text-center">
          Limit 12 referral rewards per year. See{" "}
          <Link to="/terms" className="text-magic-600 hover:underline">
            terms
          </Link>{" "}
          for details.
        </p>
      </div>
    </Card>
  );
}

// Icons
function ArrowLeftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
    </svg>
  );
}

function ExclamationIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
      />
    </svg>
  );
}

function ChartIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
      />
    </svg>
  );
}

function UsersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
      />
    </svg>
  );
}

function GiftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"
      />
    </svg>
  );
}

function SparkleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 0L14.59 8.41L23 11L14.59 13.59L12 22L9.41 13.59L1 11L9.41 8.41L12 0Z" />
    </svg>
  );
}

function ShareIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
      />
    </svg>
  );
}

function UserPlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
      />
    </svg>
  );
}

function HeartIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
    </svg>
  );
}

export default ReferralPage;
